import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';

interface StatItemProps {
  value: string;
  label: string;
  icon: string;
}

const StatItem: React.FC<StatItemProps & { delay: number }> = ({ value, label, icon, delay }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const numericVal = parseInt(value.replace(/\D/g, ''));

  useEffect(() => {
    if (!isInView) return;
    let start = 0;
    const duration = 1800;
    const step = numericVal / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= numericVal) {
        start = numericVal;
        clearInterval(timer);
      }
      setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [isInView, numericVal]);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay }}
      className="relative flex flex-col items-center justify-center p-6 rounded-3xl text-center group cursor-default"
      style={{
        background: 'rgba(255, 255, 255, 0.6)',
        border: '1px solid rgba(255, 255, 255, 0.9)',
        backdropFilter: 'blur(30px)',
        WebkitBackdropFilter: 'blur(30px)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.04)',
      }}
      whileHover={{
        borderColor: 'rgba(255, 255, 255, 1)',
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        boxShadow: '0 12px 40px rgba(0,0,0,0.08)',
        y: -4,
      }}
    >
      <span className="text-2xl mb-3 block">{icon}</span>
      <div
        className="text-3xl sm:text-4xl font-bold mb-1"
        style={{
          fontFamily: 'Syne, sans-serif',
          background: 'linear-gradient(135deg, #60a5fa, #a78bfa, #f472b6)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
        }}
      >
        {isInView ? count : 0}{value.includes('+') ? '+' : ''}
      </div>
      <div
        className="text-xs font-medium tracking-wide uppercase"
        style={{ color: '#475569', fontFamily: 'Space Grotesk, sans-serif' }}
      >
        {label}
      </div>
    </motion.div>
  );
};

const About: React.FC = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const features = [
    { icon: '🎯', title: 'Problem Solving', desc: 'Algorithmic thinking with DSA' },
    { icon: '⚡', title: 'Fast Learner', desc: 'Quick to master new stacks' },
    { icon: '🚀', title: 'Performance', desc: 'Scalable, optimized code' },
    { icon: '🤝', title: 'Collaboration', desc: 'Team player & communicator' },
  ];

  return (
    <section id="about" className="py-20 lg:py-32 relative overflow-hidden" style={{ background: '#ffffff' }}>
      {/* Background decorators */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div
          className="absolute -top-32 -right-32 w-[500px] h-[500px] opacity-30 blur-[100px]"
          style={{ background: 'radial-gradient(circle, rgba(139,92,246,0.3) 0%, transparent 70%)' }}
        />
        <div
          className="absolute -bottom-32 -left-32 w-[500px] h-[500px] opacity-20 blur-[100px]"
          style={{ background: 'radial-gradient(circle, rgba(59,130,246,0.3) 0%, transparent 70%)' }}
        />
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] opacity-10 blur-[120px]"
          style={{ background: 'radial-gradient(circle, rgba(244,114,182,0.3) 0%, transparent 70%)' }}
        />
        <div
          className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage: 'linear-gradient(rgba(0,0,0,1) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,1) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      <div ref={ref} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">

        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-16 lg:mb-20"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.5 }}
            className="inline-block mb-4"
          >
            <span className="px-4 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase" style={{
              background: 'rgba(255, 255, 255, 0.6)',
              border: '1px solid rgba(255, 255, 255, 0.9)',
              color: '#8b5cf6',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.04)',
            }}>
              Biography
            </span>
          </motion.div>

          <h2
            className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4"
            style={{ fontFamily: 'Syne, sans-serif', color: '#0f172a', letterSpacing: '-0.03em' }}
          >
            About{' '}
            <span
              style={{
                background: 'linear-gradient(135deg, #60a5fa, #a78bfa, #f472b6)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Me
            </span>
          </h2>

          <p className="text-base sm:text-lg max-w-2xl mx-auto" style={{ color: '#475569', fontFamily: 'Space Grotesk, sans-serif' }}>
            Passionate developer crafting digital experiences with clean code
          </p>
        </motion.div>

        {/* Stats grid */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-16 max-w-3xl mx-auto">
          <StatItem value="75+" label="Projects Completed" icon="🗂️" delay={0.1} />
          <StatItem value="4500+" label="Problems Solved" icon="🧠" delay={0.2} />
          <StatItem value="5+" label="Years Learning" icon="📅" delay={0.3} />
        </div>

        {/* Bento grid */}
        <div className="grid lg:grid-cols-5 gap-5">

          {/* Bio (col-span-3) */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:col-span-3 rounded-3xl p-6 sm:p-8"
            style={{
              background: 'rgba(255, 255, 255, 0.6)',
              border: '1px solid rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(30px)',
              WebkitBackdropFilter: 'blur(30px)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.04)',
            }}
          >
            {/* Label */}
            <div className="flex items-center gap-2 mb-5">
              <span className="text-sm font-semibold uppercase tracking-wider" style={{ color: '#7c3aed' }}>Background</span>
            </div>

            <div className="space-y-4">
              {[
                <>I am a dedicated <span style={{ color: '#2563eb', fontWeight: 600 }}>Backend Developer</span> with a strong foundation in Data Structures and Algorithms. I specialize in designing RESTful APIs, managing databases, and building robust, scalable web applications.</>,
                <>Beyond coding, I have a deep passion for <span style={{ color: '#7c3aed', fontWeight: 600 }}>reading books</span> and solving algorithmic challenges. Contributing to open-source projects and staying current with the evolving tech landscape is central to my growth.</>,
                <>My expertise in <span style={{ color: '#db2777', fontWeight: 600 }}>DSA & System Design</span> drives my web development journey. I'm constantly exploring the latest technologies and best practices to deliver robust, scalable applications.</>,
              ].map((text, i) => (
                <motion.p
                  key={i}
                  initial={{ opacity: 0, y: 12 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.5 + i * 0.15 }}
                  className="text-sm sm:text-base leading-relaxed"
                  style={{ color: '#475569', fontFamily: 'Space Grotesk, sans-serif' }}
                >
                  {text}
                </motion.p>
              ))}
            </div>

            {/* Tech tags */}
            <div className="mt-8 flex flex-wrap gap-2">
              {['Python', 'Django', 'React', 'TypeScript', 'Node.js', 'MySQL', 'MongoDB', 'REST APIs'].map((tech, i) => (
                <motion.span
                  key={tech}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={isInView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ delay: 0.9 + i * 0.04 }}
                  className="px-4 py-1.5 rounded-full text-xs font-medium"
                  style={{
                    background: 'rgba(255, 255, 255, 0.6)',
                    border: '1px solid rgba(255, 255, 255, 0.9)',
                    color: '#0ea5e9',
                    fontFamily: 'Space Grotesk, sans-serif',
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.04)',
                  }}
                >
                  {tech}
                </motion.span>
              ))}
            </div>
          </motion.div>

          {/* Feature cards (col-span-2) */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="lg:col-span-2 grid grid-cols-2 gap-4"
          >
            {features.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 24 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.4 + i * 0.1 }}
                whileHover={{ y: -5, scale: 1.02 }}
                className="rounded-3xl p-5 cursor-default group transition-all duration-300"
                style={{
                  background: 'rgba(255, 255, 255, 0.6)',
                  border: '1px solid rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(30px)',
                  WebkitBackdropFilter: 'blur(30px)',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.04)',
                }}
                onMouseEnter={e => {
                  (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255, 255, 255, 1)';
                  (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(255, 255, 255, 0.8)';
                  (e.currentTarget as HTMLElement).style.boxShadow = '0 12px 40px rgba(0,0,0,0.08)';
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255, 255, 255, 0.9)';
                  (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(255, 255, 255, 0.6)';
                  (e.currentTarget as HTMLElement).style.boxShadow = '0 8px 32px rgba(0,0,0,0.04)';
                }}
              >
                <div className="text-2xl mb-3 group-hover:scale-110 transition-transform duration-300">{f.icon}</div>
                <h4
                  className="font-bold text-sm mb-1"
                  style={{ fontFamily: 'Syne, sans-serif', color: '#0f172a' }}
                >
                  {f.title}
                </h4>
                <p className="text-xs leading-relaxed" style={{ color: '#475569', fontFamily: 'Space Grotesk, sans-serif' }}>
                  {f.desc}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;